/**
 * E-shop Customers Tab
 * Display and manage customers from PostgreSQL e-shop database
 */

import * as React from 'react';
import { useState, useEffect } from 'react';
import { useAuthStore } from '@store/authStore';
import { useDataSourceStore } from '@store/dataSourceStore';
import { api } from '../../services/api';
import { useWebSocket } from '@hooks/useWebSocket';
import { playNotificationSound, testNotificationSounds } from '@utils/soundNotifications';
import {
  Search,
  RefreshCw,
  Mail,
  Phone,
  Calendar,
  ShoppingBag,
  ChevronDown,
  Database,
  Grid3x3,
  List,
  Edit,
  Trash2,
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Shield,
  Filter,
  ExternalLink,
  Volume2,
} from 'lucide-react';

interface Customer {
  id: number;
  customerId: number; // E-shop customer ID (unique identifier, like session_id for sessions)
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  date_joined: string;
  total_orders: number;
  total_spent: number; // Total amount spent by customer
  last_order_date?: string;
  last_order_id?: string; // ID/number of last order
  // 🆕 Additional customer data from sync
  date_of_birth?: string;
  dob?: string;
  gender?: number; // 0=Female, 1=Male
  sex?: number; // Alias for gender
  height?: number;
  marketing_consent?: boolean;
  subscribed?: boolean;
  is_active?: boolean;
  last_login?: string;
  billing_address_id?: number;
  shipping_address_id?: number;
  getresponse_contact_id?: string; // GetResponse contact ID (if synced)
  last_synced_to_getresponse?: string; // Last sync timestamp
  // 🛒 Abandoned Cart & Consultation Fields
  cartStatus?: string; // 'active', 'abandoned', 'none'
  cartValue?: number; // Total cart value
  cartItems?: number; // Number of items in cart
  cartItemsList?: string; // Comma-separated product names
  cartAbandonedDate?: string; // When cart was abandoned
  consultationType?: string; // Type of consultation
  consultationStatus?: string; // 'abandoned', 'completed', 'none'
  consultationStartedDate?: string; // When consultation started
  customerTier?: string; // 'New', 'Returning', 'VIP', 'At Risk'
  daysSinceLastOrder?: number; // Days since last order
  // 🎯 Last event tracking
  lastEventType?: string; // Type of last event (order, login, consultation, cart, signup)
  lastEventTypeDateTime?: string; // When the last event occurred
  // 🎯 NEW: Rich Customer Analytics
  customerMetrics?: {
    daysSinceJoined: number;
    daysSinceLastOrder: number | null;
    isNewCustomer: boolean;
    isReturningCustomer: boolean;
    isVIPCustomer: boolean;
    isAtRiskCustomer: boolean;
    customerSegment: string;
    estimatedLifetimeValue: number;
    avgOrderValue: number;
    monthlyOrderFrequency: number;
    customerAge: string;
    totalSpent: number;
  };
}

interface EshopCustomersTabProps {
  eshopId: string;
  eshopConfig?: any; // E-shop configuration (includes shopUrl for order links)
}

// 🚀 PERFORMANCE: Memoized table row component - only re-renders when THIS customer's data changes
interface CustomerRowProps {
  customer: Customer;
  eshopConfig?: any;
  onViewDetails: (customer: Customer) => void;
  onEdit: (customer: Customer) => void;
  onDelete: (customer: Customer) => void;
}

const CustomerRow = React.memo<CustomerRowProps>(
  ({ customer, eshopConfig, onViewDetails, onEdit, onDelete }) => {
    return (
      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
        {/* Customer Name + Contact */}
        <td className="px-3 py-2">
          <a
            href={`https://quickmeds.co.uk/dash/users/${customer.id}/`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            {customer.first_name} {customer.last_name}
          </a>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            ID: {customer.id}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-xs">
            {customer.email}
          </div>
          {customer.phone_number && (
            <div className="text-xs text-gray-600 dark:text-gray-400">
              {customer.phone_number}
            </div>
          )}
        </td>
        
        {/* Orders */}
        <td className="px-3 py-2">
          <div className="font-medium text-gray-900 dark:text-white">
            {customer.total_orders}
          </div>
          {customer.last_order_date && (
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {new Date(customer.last_order_date).toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          )}
          {(customer.daysSinceLastOrder !== undefined || customer.days_since_last_order !== undefined || customer.customerMetrics?.daysSinceLastOrder !== undefined) && (
            <div className="text-xs text-purple-600 dark:text-purple-400 font-medium mt-0.5">
              {customer.daysSinceLastOrder ?? customer.days_since_last_order ?? customer.customerMetrics?.daysSinceLastOrder} days ago
            </div>
          )}
        </td>
        
        {/* Last Order ID */}
        <td className="px-3 py-2">
          {customer.last_order_id ? (
            (() => {
              const orderIdMatch = customer.last_order_id.match(/\d+/);
              const numericOrderId = orderIdMatch ? parseInt(orderIdMatch[0], 10) : null;
              
              return numericOrderId ? (
                <a
                  href={`https://quickmeds.co.uk/dash/orders/${numericOrderId}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                  title={`Open order ${customer.last_order_id}`}
                >
                  #{customer.last_order_id}
                </a>
              ) : (
                <span className="text-sm text-gray-900 dark:text-white">
                  #{customer.last_order_id}
                </span>
              );
            })()
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </td>
        
        {/* Joined Date */}
        <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
          {new Date(customer.date_joined).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </td>
        
        {/* Customer Value */}
        <td className="px-3 py-2">
          {customer.customerMetrics ? (
            <div>
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  customer.customerMetrics.customerSegment === 'VIP'
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    : customer.customerMetrics.customerSegment === 'At Risk'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    : customer.customerMetrics.customerSegment === 'Returning'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                }`}
              >
                {customer.customerMetrics.customerSegment}
              </span>
              <div className="text-sm font-semibold text-green-600 mt-1">
                £{customer.customerMetrics.totalSpent || customer.customerMetrics.estimatedLifetimeValue}
              </div>
            </div>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </td>
        
        {/* DOB / Gender */}
        <td className="px-3 py-2">
          {customer.date_of_birth || customer.dob ? (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {new Date(customer.date_of_birth || customer.dob || '').toLocaleDateString('en-GB')}
            </div>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
          {(customer.gender !== undefined || customer.sex !== undefined) && (
            <span
              className={`inline-block px-2 py-0.5 rounded text-xs font-medium mt-0.5 ${
                customer.gender === 1 || customer.sex === 1
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400'
              }`}
            >
              {customer.gender === 1 || customer.sex === 1 ? 'F' : 'M'}
            </span>
          )}
        </td>
        
        {/* Consent */}
        <td className="px-3 py-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            customer.marketing_consent || customer.subscribed
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
          }`}>
            {customer.marketing_consent || customer.subscribed ? 'Yes' : 'No'}
          </span>
        </td>
        
        {/* Status */}
        <td className="px-3 py-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            customer.is_active !== false
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
          }`}>
            {customer.is_active !== false ? 'Active' : 'Inactive'}
          </span>
        </td>
        
        {/* GetResponse */}
        <td className="px-3 py-2">
          {customer.getresponse_contact_id ? (
            <div>
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                Synced
              </span>
              <div className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">
                {customer.getresponse_contact_id.substring(0, 6)}
              </div>
            </div>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </td>
        
        {/* Cart */}
        <td className="px-3 py-2">
          {customer.cartStatus && customer.cartStatus !== 'none' ? (
            <div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                customer.cartStatus === 'abandoned'
                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                  : customer.cartStatus === 'converted'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              }`}>
                {customer.cartStatus}
              </span>
              {customer.cartValue !== undefined && customer.cartValue > 0 && (
                <div className="text-xs font-semibold text-gray-900 dark:text-white mt-0.5">
                  £{customer.cartValue.toFixed(2)}
                </div>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </td>
        
        {/* Cart Items */}
        <td className="px-3 py-2">
          {customer.cartItemsList && customer.cartItemsList.trim() !== '' ? (
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-900 dark:text-white">
                {customer.cartItems || 0} item{customer.cartItems !== 1 ? 's' : ''}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-xs" title={customer.cartItemsList}>
                {customer.cartItemsList}
              </div>
            </div>
          ) : customer.cartItems && customer.cartItems > 0 ? (
            <div className="text-xs font-medium text-gray-900 dark:text-white">
              {customer.cartItems} item{customer.cartItems !== 1 ? 's' : ''}
            </div>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </td>
        
        {/* Consultation */}
        <td className="px-3 py-2">
          {customer.consultationStatus && customer.consultationStatus !== 'none' ? (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              customer.consultationStatus === 'abandoned'
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
            }`}>
              {customer.consultationStatus}
            </span>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </td>
        
        {/* Last Event */}
        <td className="px-3 py-2">
          {customer.lastEventType ? (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              customer.lastEventType === 'order' || customer.lastEventType === 'order_placed'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : customer.lastEventType.includes('cart')
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
            }`}>
              {customer.lastEventType}
            </span>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </td>
        
        {/* Last Event Date */}
        <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
          {customer.lastEventTypeDateTime ? (
            new Date(customer.lastEventTypeDateTime).toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </td>
        
        {/* Actions */}
        <td className="px-3 py-2">
          <div className="flex items-center gap-1">
            <button
              onClick={() => onViewDetails(customer)}
              className="p-1 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded"
              title="View"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={() => onEdit(customer)}
              className="p-1 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
              title="Edit"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(customer)}
              className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  }
);

CustomerRow.displayName = 'CustomerRow';

const EshopCustomersTab: React.FC<EshopCustomersTabProps> = ({ eshopId, eshopConfig }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSyncDropdown, setShowSyncDropdown] = useState(false);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [sortBy, setSortBy] = useState('lastEventTypeDateTime');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false); // Collapsible filters
  
  // 🔧 Use ref to prevent race conditions on fetch
  const fetchingRef = React.useRef(false);

  // 🎯 NEW: Filters
  const [filterConsent, setFilterConsent] = useState<'all' | 'opted_in' | 'no_consent'>('all');
  const [filterGender, setFilterGender] = useState<'all' | 'male' | 'female'>('all');
  const [filterAge, setFilterAge] = useState<'all' | '18-30' | '31-40' | '41-50' | '51-60' | '61+'>(
    'all'
  );
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterOrders, setFilterOrders] = useState<'all' | '0' | '1-5' | '6-10' | '11-20' | '21+'>(
    'all'
  );
  const [filterCustomerType, setFilterCustomerType] = useState<
    'all' | 'new' | 'returning' | 'vip' | 'at-risk'
  >('all');
  const [filterCartStatus, setFilterCartStatus] = useState<'all' | 'active' | 'abandoned' | 'none'>(
    'all'
  );
  const [filterConsultationStatus, setFilterConsultationStatus] = useState<
    'all' | 'abandoned' | 'completed' | 'none'
  >('all');
  // 🎯 NEW: Event Type Filter
  const [filterEventType, setFilterEventType] = useState<
    | 'all'
    | 'signup'
    | 'login'
    | 'order'
    | 'cart_created'
    | 'cart_abandoned'
    | 'consultation_completed'
    | 'consultation_abandoned'
  >('all');
  // 🎯 NEW: Date Range Filter - DEFAULT TO 'all_time' to show all 28K customers
  const [dateRangeFilter, setDateRangeFilter] = useState('all_time');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  // 🎯 NEW: Product Filter
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const productDropdownRef = React.useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const { accessToken } = useAuthStore();
  const { getSelection } = useDataSourceStore();

  // Get the selected company from the data source store - MEMOIZED to prevent re-renders
  const selection = React.useMemo(() => getSelection(), [getSelection]);
  const companyId = selection?.companyId;

  // 📡 WebSocket for real-time customer updates
  const { socket, connected } = useWebSocket({ autoConnect: true });

  // 🔥 VERSION MARKER - Only log once on mount
  React.useEffect(() => {
    console.log('🔥 ESHOP CUSTOMERS TAB MOUNTED - VERSION: 2025-12-03-14:32 🔥');
    console.log('🔍 EshopCustomersTab Initial State:', {
      eshopId,
      companyId,
      hasToken: !!accessToken,
    });
  }, []);

  // Debug: Track dropdown state changes
  React.useEffect(() => {
    console.log('🔍 Sync Dropdown State Changed:', showSyncDropdown);
  }, [showSyncDropdown]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        console.log('🎯 Clicked outside dropdown - closing');
        setShowSyncDropdown(false);
      }
    };

    if (showSyncDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSyncDropdown]);

  // Close product dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target as Node)
      ) {
        setShowProductDropdown(false);
      }
    };

    if (showProductDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductDropdown]);

  // Fetch ALL unique products from backend (not just current page!)
  const [allProducts, setAllProducts] = React.useState<string[]>([]);
  const [loadingProducts, setLoadingProducts] = React.useState(false);

  React.useEffect(() => {
    const fetchAllProducts = async () => {
      if (!companyId || !eshopId) return;

      setLoadingProducts(true);
      try {
        const response = await api.get(
          `/api/v1/eshops/${eshopId}/unique-products?companyId=${companyId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (response.data.success) {
          const products = response.data.data || [];
          setAllProducts(products);
          console.log(`📦 Loaded ${products.length} unique products from ALL customers`);
        }
      } catch (error) {
        console.error('Failed to fetch unique products:', error);
        setAllProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchAllProducts();
  }, [companyId, eshopId, accessToken]);

  // Filter products by search term for autocomplete
  const filteredProducts = React.useMemo(() => {
    if (!productSearchTerm) return allProducts;
    return allProducts.filter(p => p.toLowerCase().includes(productSearchTerm.toLowerCase()));
  }, [allProducts, productSearchTerm]);

  // Apply client-side product filtering to backend-filtered customers
  const displayedCustomers = React.useMemo(() => {
    // Safety check: ensure customers is an array
    if (!Array.isArray(customers)) {
      console.warn('⚠️ customers is not an array:', customers);
      return [];
    }
    
    if (selectedProducts.length === 0) return customers;

    return customers.filter(customer => {
      if (!customer.cartItemsList || customer.cartItemsList.trim() === '') return false;

      const customerProducts = customer.cartItemsList
        .split(',')
        .map(p => p.trim())
        .filter(p => p);
      return selectedProducts.some(selectedProduct => customerProducts.includes(selectedProduct));
    });
  }, [customers, selectedProducts]);

  // 🔧 DEFINE fetchCustomers BEFORE using it in useEffect
  const fetchCustomers = React.useCallback(async () => {
    console.log('🔍 fetchCustomers called with filterOrders:', filterOrders);
    
    // 🔒 Prevent race conditions - if already fetching, skip
    if (fetchingRef.current) {
      console.log('⏭️ Skipping fetch - already in progress');
      return;
    }
    
    try {
      fetchingRef.current = true;
      setLoading(true);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        search: searchTerm,
        sortBy: sortBy,
        sortOrder: sortOrder,
        companyId: companyId || '',
        filterConsent: filterConsent,
        filterGender: filterGender,
        filterAge: filterAge,
        filterStatus: filterStatus,
        filterOrders: filterOrders,
        filterCustomerType: filterCustomerType,
        filterCartStatus: filterCartStatus,
        filterConsultationStatus: filterConsultationStatus,
        filterEventType: filterEventType,
        dateRangeFilter: dateRangeFilter,
        customDateFrom: customDateFrom,
        customDateTo: customDateTo,
      });

      const response = await api.get(`/api/v1/eshops/${eshopId}/customers?${params}`);

      const data = response.data;
      
      // 🛡️ Safety checks for pagination data
      const customersData = data?.data?.customers;
      const paginationData = data?.data?.pagination;
      
      if (!Array.isArray(customersData)) {
        console.error('❌ Invalid customers data received:', customersData);
        setCustomers([]);
        setTotalPages(1);
        setTotalCount(0);
        return;
      }
      
      setCustomers(customersData);
      setTotalPages(paginationData?.totalPages || 1);
      setTotalCount(paginationData?.total || 0);
      
      console.log(`✅ Loaded ${customersData.length} customers (page ${page}/${paginationData?.totalPages || 1})`);
      console.log(`📊 Pagination data:`, { totalPages: paginationData?.totalPages, totalCount: paginationData?.total, page });
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
      setTotalPages(1); // Set to 1 instead of leaving undefined
      setTotalCount(0);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [
    eshopId,
    page,
    searchTerm,
    sortBy,
    sortOrder,
    companyId,
    filterConsent,
    filterGender,
    filterAge,
    filterStatus,
    filterOrders,
    filterCustomerType,
    filterCartStatus,
    filterConsultationStatus,
    filterEventType,
    dateRangeFilter,
    customDateFrom,
    customDateTo,
  ]); // ✅ REMOVED 'loading' from dependencies to prevent infinite loops

  // 🔧 FIX: Simplified useEffect - all dependencies are now in fetchCustomers
  useEffect(() => {
    console.log('🔍 useEffect triggered! Calling fetchCustomers...');
    fetchCustomers();
  }, [fetchCustomers]); // Only depend on the memoized function
  
  // 🐛 DEBUG: Log pagination state changes
  useEffect(() => {
    console.log('📊 Pagination state updated:', { totalCount, totalPages, page, customersCount: customers.length });
  }, [totalCount, totalPages, page, customers.length]);

  // 📡 WebSocket: Subscribe to customer updates
  useEffect(() => {
    if (!socket || !connected || !companyId || !eshopId) {
      console.log('⚠️ WebSocket not ready for customers:', {
        hasSocket: !!socket,
        connected,
        hasCompanyId: !!companyId,
        hasEshopId: !!eshopId,
      });
      return;
    }

    // Subscribe to customers room
    console.log(`📡 Subscribing to customers room: customers:${companyId}:${eshopId}`);
    socket.emit('customers:subscribe', { companyId, dataSourceId: eshopId });

    // Listen for customer updates
    const handleCustomerUpdate = (data: { customer: Customer; timestamp: string }) => {
      console.log(
        '🔔 Customer updated via WebSocket:',
        data.customer.email,
        'customerId:',
        data.customer.customerId
      );

      setCustomers(prevCustomers => {
        // 🔒 Safety check: ensure prevCustomers is an array
        if (!Array.isArray(prevCustomers)) {
          console.error('❌ prevCustomers is not an array, returning empty array');
          return [];
        }
        
        // Check if customer already exists (match by BOTH customerId AND email for safety!)
        const existingIndex = prevCustomers.findIndex(
          c => c.customerId === data.customer.customerId || c.email === data.customer.email
        );

        if (existingIndex >= 0) {
          const existing = prevCustomers[existingIndex];

          console.log(`🔄 Updating existing customer at index ${existingIndex}:`, {
            oldEmail: existing.email,
            newEmail: data.customer.email,
            oldCustomerId: existing.customerId,
            newCustomerId: data.customer.customerId,
          });

          // MERGE update with existing customer (preserve all fields)
          const mergedCustomer = {
            ...existing, // Keep existing data
            ...data.customer, // Merge with new data (overwrite with WebSocket update)
          };

          // 🔊 Detect events and play sounds
          // Check if cart items increased (Add to Cart)
          if (
            mergedCustomer.cartItems !== undefined &&
            existing.cartItems !== undefined &&
            mergedCustomer.cartItems > existing.cartItems
          ) {
            console.log('🛒 Cart items increased - playing cart add sound');
            playNotificationSound('cart-add', 0.3);
          }

          // Check if total orders increased (Order Placed)
          if (
            mergedCustomer.total_orders !== undefined &&
            existing.total_orders !== undefined &&
            mergedCustomer.total_orders > existing.total_orders
          ) {
            console.log('✅ Order placed - playing order success sound');
            playNotificationSound('order-placed', 0.4);
          }

          // Check if cart was abandoned
          if (
            existing.cartStatus !== 'cart_abandoned' &&
            mergedCustomer.cartStatus === 'cart_abandoned'
          ) {
            console.log('⚠️ Cart abandoned - playing notification');
            playNotificationSound('cart-abandoned', 0.2);
          }

          console.log('✅ Applying WebSocket update - moving customer to top of list');

          // 🔝 Move updated customer to TOP of list (most recent update first!)
          const updated = [...prevCustomers];
          updated.splice(existingIndex, 1); // Remove from current position
          updated.unshift(mergedCustomer); // Add to top of list
          return updated;
        } else {
          // New customer not on current page - check if it's a brand new customer
          if (data.customer.total_orders === 0 && data.customer.cartItems > 0) {
            console.log('👋 New customer with cart - playing new customer sound');
            playNotificationSound('new-customer', 0.3);
          }
          
          // New customer not on current page - ignore it (will be fetched when paginating)
          console.log('⏭️ New customer not on current page - ignoring');
          return prevCustomers;
        }
      });
    };

    socket.on('customers:updated', handleCustomerUpdate);

    // Listen for subscription confirmation
    socket.on('customers:subscribed', (data: { room: string }) => {
      console.log('✅ Successfully subscribed to customers room:', data.room);
    });

    return () => {
      console.log('🔌 Unsubscribing from customers room');
      socket.off('customers:updated', handleCustomerUpdate);
      socket.off('customers:subscribed');
      socket.emit('customers:unsubscribe', { companyId, dataSourceId: eshopId });
    };
  }, [socket, connected, companyId, eshopId]);

  const triggerSync = React.useCallback(
    async (forceFullSync = false) => {
      console.log('🚨 SYNC BUTTON CLICKED!', {
        forceFullSync,
        companyId,
        eshopId,
        hasToken: !!accessToken,
      });

      try {
        setSyncing(true);
        const syncType = forceFullSync ? 'Full re-sync' : 'Incremental sync';

        // Validate required parameters
        if (!companyId) {
          console.error('❌ Missing companyId:', companyId);
          alert('❌ Company ID is missing! Please select a company first.');
          return;
        }

        if (!accessToken) {
          console.error('❌ Missing accessToken');
          alert('❌ Access token is missing! Please re-login.');
          return;
        }

        console.log(`🔄 Starting ${syncType}...`, {
          eshopId,
          companyId,
          forceFullSync,
        });

        const syncUrl = `/api/v1/eshops/${eshopId}/sync?companyId=${companyId}&syncType=customers${
          forceFullSync ? '&forceFullSync=true' : ''
        }`;

        console.log(`📡 Sync URL: ${syncUrl}`);
        console.log(`📡 Making POST request NOW...`);

        const response = await api.post(syncUrl);

        console.log(`📊 Response received! Status: ${response.status}`);

        const result = response.data;
        console.log('✅ Customers sync completed:', result);

        // Refresh data after sync
        await fetchCustomers();
        alert(
          `✅ ${syncType} completed successfully! ${
            result.data?.synced || result.data?.stats?.synced || 0
          } customers synced.`
        );
      } catch (error: any) {
        console.error('❌ Error triggering sync:', error);
        alert(`❌ Sync failed: ${error.message}\n\nCheck browser console for details.`);
      } finally {
        setSyncing(false);
      }
    },
    [companyId, eshopId, accessToken, fetchCustomers]
  ); // useCallback dependencies

  // 🚀 PERFORMANCE: Memoized action handlers to prevent CustomerRow re-renders
  const handleViewDetails = React.useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
  }, []);
  
  const handleEdit = React.useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    setShowEditModal(true);
  }, []);
  
  const handleDelete = React.useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    setShowDeleteModal(true);
  }, []);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1); // Reset to first page on search
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to desc
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(1); // Reset to first page on sort
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="w-3 h-3 text-gray-400" />;
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
    ) : (
      <ArrowDown className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
    );
  };

  return (
    <div className="space-y-4">
      {/* Search Bar & Filters Button */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={e => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-4 py-2.5 rounded-lg border transition-colors flex items-center gap-2 ${
            showFilters
              ? 'bg-indigo-50 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-600 dark:text-indigo-400'
              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Filter className="w-5 h-5" />
          Filters
        </button>
        <button
          onClick={testNotificationSounds}
          className="px-4 py-2.5 rounded-lg border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
          title="Test notification sounds"
        >
          <Volume2 className="w-5 h-5" />
          Test Sounds
        </button>
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'list'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
            title="List view"
          >
            <List className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'card'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
            title="Card view"
          >
            <Grid3x3 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filters - Responsive & Stackable - COLLAPSIBLE */}
      {showFilters && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* All Filter Dropdowns */}
            <select
              value={filterConsent}
              onChange={e => {
                setFilterConsent(e.target.value as any);
                setPage(1);
              }}
              className="w-full sm:w-auto px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all">All Consent</option>
              <option value="opted_in">✓ Opted In</option>
              <option value="no_consent">✗ No Consent</option>
            </select>

            <select
              value={filterGender}
              onChange={e => {
                setFilterGender(e.target.value as any);
                setPage(1);
              }}
              className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all">All Gender</option>
              <option value="male">♂ Male</option>
              <option value="female">♀ Female</option>
            </select>

            <select
              value={filterAge}
              onChange={e => {
                setFilterAge(e.target.value as any);
                setPage(1);
              }}
              className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all">All Ages</option>
              <option value="18-30">18-30</option>
              <option value="31-40">31-40</option>
              <option value="41-50">41-50</option>
              <option value="51-60">51-60</option>
              <option value="61+">61+</option>
            </select>

            <select
              value={filterStatus}
              onChange={e => {
                setFilterStatus(e.target.value as any);
                setPage(1);
              }}
              className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all">All Status</option>
              <option value="active">✓ Active</option>
              <option value="inactive">✗ Inactive</option>
            </select>

            <select
              value={filterOrders}
              onChange={e => {
                console.log('🔍 Order filter changed to:', e.target.value);
                setFilterOrders(e.target.value as any);
                setPage(1);
              }}
              className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all">All Orders</option>
              <option value="0">0 orders</option>
              <option value="1-5">1-5 orders</option>
              <option value="6-10">6-10 orders</option>
              <option value="11-20">11-20 orders</option>
              <option value="21+">21+ orders</option>
            </select>

            <select
              value={filterCustomerType}
              onChange={e => {
                setFilterCustomerType(e.target.value as any);
                setPage(1);
              }}
              className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all">All Types</option>
              <option value="new">🆕 New</option>
              <option value="returning">🔄 Returning</option>
              <option value="vip">⭐ VIP</option>
              <option value="at-risk">⚠️ At Risk</option>
            </select>

            <select
              value={filterCartStatus}
              onChange={e => {
                setFilterCartStatus(e.target.value as any);
                setPage(1);
              }}
              className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all">All Carts</option>
              <option value="active">🛒 Active Cart</option>
              <option value="abandoned">⚠️ Abandoned Cart</option>
              <option value="converted">✅ Converted to Order</option>
              <option value="none">✗ No Cart</option>
            </select>

            <select
              value={filterConsultationStatus}
              onChange={e => {
                setFilterConsultationStatus(e.target.value as any);
                setPage(1);
              }}
              className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all">All Consultations</option>
              <option value="abandoned">⚠️ Abandoned Consultation</option>
              <option value="completed">✓ Completed Consultation</option>
              <option value="none">✗ No Consultation</option>
            </select>

            {/* 🎯 NEW: Last Event Type Filter */}
            <select
              value={filterEventType}
              onChange={e => {
                setFilterEventType(e.target.value as any);
                setPage(1);
              }}
              className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all">All Events</option>
              <option value="signup">🆕 Signup</option>
              <option value="login">🔐 Login</option>
              <option value="order">🛍️ Order Placed</option>
              <option value="cart_created">🛒 Cart Created</option>
              <option value="cart_abandoned">⚠️ Cart Abandoned</option>
              <option value="consultation_completed">✅ Consultation Completed</option>
              <option value="consultation_abandoned">⏸️ Consultation Abandoned</option>
            </select>

            {/* 🎯 NEW: Date Range Filter */}
            <select
              value={dateRangeFilter}
              onChange={e => {
                setDateRangeFilter(e.target.value);
                setPage(1);
              }}
              className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all_time">All Time</option>
              <option value="today">Today</option>
              <option value="last_7_days">Last 7 days</option>
              <option value="last_14_days">Last 14 days</option>
              <option value="last_30_days">Last 30 days</option>
              <option value="last_60_days">Last 60 days</option>
              <option value="last_90_days">Last 90 days</option>
              <option value="custom">Custom Range</option>
            </select>

            {/* 🎯 Custom Date Range Inputs (show when "custom" selected) */}
            {dateRangeFilter === 'custom' && (
              <>
                <input
                  type="date"
                  value={customDateFrom}
                  onChange={e => {
                    setCustomDateFrom(e.target.value);
                    setPage(1);
                  }}
                  className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
                  placeholder="From"
                />
                <input
                  type="date"
                  value={customDateTo}
                  onChange={e => {
                    setCustomDateTo(e.target.value);
                    setPage(1);
                  }}
                  className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
                  placeholder="To"
                />
              </>
            )}

            {/* 🎯 NEW: Product Multi-Select Filter */}
            <div ref={productDropdownRef} className="relative w-full sm:w-auto min-w-[200px]">
              <button
                onClick={() => setShowProductDropdown(!showProductDropdown)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm text-left flex items-center justify-between"
              >
                <span
                  className={
                    selectedProducts.length === 0 ? 'text-gray-500 dark:text-gray-400' : ''
                  }
                >
                  {selectedProducts.length === 0
                    ? '📦 Filter by Product'
                    : `${selectedProducts.length} product${
                        selectedProducts.length > 1 ? 's' : ''
                      } selected`}
                </span>
                <ChevronDown className="w-4 h-4 ml-2" />
              </button>

              {showProductDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full min-w-[300px] max-w-[400px] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-[300px] overflow-hidden">
                  {/* Search Input */}
                  <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearchTerm}
                      onChange={e => setProductSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm placeholder-gray-500 dark:placeholder-gray-400"
                      onClick={e => e.stopPropagation()}
                    />
                  </div>

                  {/* Product List */}
                  <div className="overflow-y-auto max-h-[220px]">
                    {loadingProducts ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-2" />
                        Loading products...
                      </div>
                    ) : filteredProducts.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        {allProducts.length === 0 ? 'No products found' : 'No matching products'}
                      </div>
                    ) : (
                      filteredProducts.map(product => (
                        <label
                          key={product}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={selectedProducts.includes(product)}
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedProducts([...selectedProducts, product]);
                              } else {
                                setSelectedProducts(selectedProducts.filter(p => p !== product));
                              }
                            }}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <span className="text-gray-900 dark:text-gray-100 truncate">
                            {product}
                          </span>
                        </label>
                      ))
                    )}
                  </div>

                  {/* Footer Actions */}
                  {selectedProducts.length > 0 && (
                    <div className="p-2 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {selectedProducts.length} selected
                      </span>
                      <button
                        onClick={() => setSelectedProducts([])}
                        className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Clear Filters Button */}
            {(filterConsent !== 'all' ||
              filterGender !== 'all' ||
              filterAge !== 'all' ||
              filterStatus !== 'all' ||
              filterOrders !== 'all' ||
              filterCustomerType !== 'all' ||
              filterCartStatus !== 'all' ||
              filterConsultationStatus !== 'all' ||
              filterEventType !== 'all' ||
              dateRangeFilter !== 'all_time' ||
              selectedProducts.length > 0) && (
              <button
                onClick={() => {
                  setFilterConsent('all');
                  setFilterGender('all');
                  setFilterAge('all');
                  setFilterStatus('all');
                  setFilterOrders('all');
                  setFilterCustomerType('all');
                  setFilterCartStatus('all');
                  setFilterConsultationStatus('all');
                  setFilterEventType('all');
                  setDateRangeFilter('all_time');
                  setCustomDateFrom('');
                  setCustomDateTo('');
                  setSelectedProducts([]);
                  setPage(1);
                }}
                className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                title="Clear all filters"
              >
                <RefreshCw className="w-3 h-3" />
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Stats Summary */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Customers</h3>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Total:{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {totalCount.toLocaleString()}
            </span>{' '}
            customers
          </div>
        </div>
      </div>

      {/* Legacy sync dropdown - hidden */}
      <div className="hidden">
        <div className="flex items-center gap-2">
          {/* Old E-shop Sync Options Dropdown - KEEP FOR BACKUP */}
          <div className="relative" ref={dropdownRef} style={{ display: 'none' }}>
            <button
              onClick={e => {
                e.stopPropagation();
                console.log('🚨 SYNC OPTIONS BUTTON CLICKED!');
                setShowSyncDropdown(!showSyncDropdown);
              }}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Options'}
              <ChevronDown className="w-4 h-4" />
            </button>

            {showSyncDropdown && !syncing && (
              <div
                className="absolute right-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10"
                onMouseEnter={() => console.log('🎯 Mouse entered dropdown')}
                onClick={e => {
                  e.stopPropagation();
                  console.log('🎯 Dropdown wrapper clicked');
                }}
              >
                <button
                  onClick={e => {
                    e.stopPropagation();
                    console.log('🚨 INCREMENTAL SYNC CLICKED!');
                    setShowSyncDropdown(false);
                    triggerSync(false);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 border-b border-gray-200 dark:border-gray-700"
                >
                  <RefreshCw className="w-4 h-4 text-blue-600" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      Incremental Sync
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Sync only new/updated customers
                    </div>
                  </div>
                </button>

                <button
                  onClick={e => {
                    e.stopPropagation();
                    console.log('🚨 FORCE FULL RE-SYNC CLICKED!');
                    setShowSyncDropdown(false);
                    if (
                      confirm(
                        '⚠️  Force Full Re-sync will clear existing customer data and recalculate order counts. This may take a few minutes. Continue?'
                      )
                    ) {
                      triggerSync(true);
                    }
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                >
                  <Database className="w-4 h-4 text-orange-600" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      Force Full Re-sync
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Recalculate all customer data & order counts
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Customers Data (Card or List View) */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      ) : displayedCustomers.length === 0 ? (
        <div className="text-center py-12 text-gray-600 dark:text-gray-400">
          {selectedProducts.length > 0
            ? `No customers found with selected products (${selectedProducts.length} product${
                selectedProducts.length > 1 ? 's' : ''
              } selected)`
            : 'No customers found'}
        </div>
      ) : viewMode === 'card' ? (
        /* CARD VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayedCustomers.map(customer => (
            <div
              key={`${customer.id}-${customer.email}`}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg transition-shadow"
            >
              {/* Customer Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {customer.first_name} {customer.last_name}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">ID: {customer.id}</p>
                </div>
                <div
                  title={
                    customer.marketing_consent || customer.subscribed ? 'Opted In' : 'No Consent'
                  }
                >
                  {customer.marketing_consent || customer.subscribed ? (
                    <Shield className="w-5 h-5 text-green-500" />
                  ) : (
                    <Shield className="w-5 h-5 text-red-500" />
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-3">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-3 h-3 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400 truncate">
                    {customer.email}
                  </span>
                </div>
                {customer.phone_number && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-3 h-3 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">
                      {customer.phone_number}
                    </span>
                  </div>
                )}
                {(customer.date_of_birth || customer.dob) && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">
                      {new Date(customer.date_of_birth || customer.dob || '').toLocaleDateString(
                        'en-GB'
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-900 dark:text-white">
                    {customer.total_orders}
                  </div>
                  <div className="text-xs text-gray-500">Orders</div>
                </div>
                {(customer.gender !== undefined || customer.sex !== undefined) && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      customer.gender === 1 || customer.sex === 1
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400'
                    }`}
                  >
                    {customer.gender === 1 || customer.sex === 1 ? 'Female' : 'Male'}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => {
                    setSelectedCustomer(customer);
                    setShowDetailsModal(true);
                  }}
                  className="flex-1 px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1"
                >
                  <Eye className="w-3 h-3" />
                  View
                </button>
                <button
                  onClick={() => {
                    setSelectedCustomer(customer);
                    setShowEditModal(true);
                  }}
                  className="flex-1 px-3 py-1.5 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors flex items-center justify-center gap-1"
                >
                  <Edit className="w-3 h-3" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    setSelectedCustomer(customer);
                    setShowDeleteModal(true);
                  }}
                  className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* LIST VIEW - DataTable */
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th
                    onClick={() => handleSort('first_name')}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Customer
                      <SortIcon column="first_name" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('total_orders')}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Orders
                      <SortIcon column="total_orders" />
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Last Order ID
                  </th>
                  <th
                    onClick={() => handleSort('date_joined')}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Joined
                      <SortIcon column="date_joined" />
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Customer Value
                  </th>
                  <th
                    onClick={() => handleSort('date_of_birth')}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      DOB / Gender
                      <SortIcon column="date_of_birth" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('marketing_consent')}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Consent
                      <SortIcon column="marketing_consent" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('is_active')}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Status
                      <SortIcon column="is_active" />
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    GetResponse
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Cart
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Cart Items
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Consultation
                  </th>
                  <th
                    onClick={() => handleSort('lastEventType')}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Last Event
                      <SortIcon column="lastEventType" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('lastEventTypeDateTime')}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Last Event Date
                      <SortIcon column="lastEventTypeDateTime" />
                    </div>
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {/* 🚀 PERFORMANCE: Use memoized CustomerRow - only changed rows re-render! */}
                {displayedCustomers.map(customer => (
                  <CustomerRow
                    key={`${customer.id}-${customer.email}`}
                    customer={customer}
                    eshopConfig={eshopConfig}
                    onViewDetails={handleViewDetails}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination (for both Card and List views) */}
      {/* 🛡️ Show pagination even on page 1 if there are customers, hide only if no data */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg mt-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {totalPages} • Showing {displayedCustomers?.length || 0}
            {selectedProducts.length > 0 ? ` (filtered)` : ''} of {totalCount.toLocaleString()}{' '}
            customers
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ← Previous
            </button>
            <div className="flex items-center gap-2 px-3 text-sm text-gray-600 dark:text-gray-400">
              Page
              <input
                type="number"
                min="1"
                max={totalPages}
                value={page}
                onChange={e => {
                  const newPage = parseInt(e.target.value);
                  if (newPage >= 1 && newPage <= totalPages) {
                    setPage(newPage);
                  }
                }}
                className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-center bg-white dark:bg-gray-700 dark:text-white"
              />
              of {totalPages}
            </div>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showDetailsModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full my-8">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4 flex items-center justify-between rounded-t-lg z-10">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                Customer Details - {selectedCustomer.first_name} {selectedCustomer.last_name}
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="px-4 sm:px-6 py-4 space-y-4 max-h-[calc(90vh-180px)] overflow-y-auto">
              {/* Statistics Comparison */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-3 sm:p-4 border border-indigo-200 dark:border-indigo-800">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2 text-sm">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Customer Statistics
                </h4>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Spent</div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      £{(
                        selectedCustomer.total_spent ||
                        selectedCustomer.customerMetrics?.totalSpent ||
                        selectedCustomer.customerMetrics?.estimatedLifetimeValue ||
                        0
                      ).toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Lifetime value
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Orders</div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {selectedCustomer.total_orders || 0}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Orders placed
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg Order Value</div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      £{(() => {
                        const totalSpent = selectedCustomer.total_spent || selectedCustomer.customerMetrics?.totalSpent || selectedCustomer.customerMetrics?.estimatedLifetimeValue || 0;
                        const totalOrders = selectedCustomer.total_orders || 0;
                        return totalOrders > 0 ? (totalSpent / totalOrders).toFixed(2) : '0.00';
                      })()}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Per order average
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Customer Tier</div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                      {selectedCustomer.customerTier || selectedCustomer.customer_tier || selectedCustomer.customerMetrics?.customerSegment || 'Standard'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {selectedCustomer.customerMetrics?.daysSinceJoined 
                        ? `Member ${selectedCustomer.customerMetrics.daysSinceJoined} days`
                        : 'Loyalty status'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-3">
                  {/* Personal Info */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">Personal Information</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Customer ID:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">{selectedCustomer.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Full Name:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.first_name} {selectedCustomer.last_name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Email:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white truncate max-w-[200px]">
                          {selectedCustomer.email}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Phone:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.phone_number || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Date of Birth:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.date_of_birth || selectedCustomer.dob
                            ? new Date(selectedCustomer.date_of_birth || selectedCustomer.dob || '').toLocaleDateString('en-GB')
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Gender:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.gender === 1 || selectedCustomer.sex === 1
                            ? 'Male'
                            : selectedCustomer.gender === 0 || selectedCustomer.sex === 0
                            ? 'Female'
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Height:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.height ? `${selectedCustomer.height} cm` : '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Address Info */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">Address Information</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Billing Address ID:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.billing_address_id || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Shipping Address ID:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.shipping_address_id || '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Order Stats */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">Order History</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Total Orders:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">{selectedCustomer.total_orders || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Total Spent:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          £{(
                            selectedCustomer.total_spent ||
                            selectedCustomer.customerMetrics?.totalSpent ||
                            selectedCustomer.customerMetrics?.estimatedLifetimeValue ||
                            0
                          ).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Last Order Date:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.last_order_date
                            ? new Date(selectedCustomer.last_order_date).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Last Order ID:</span>
                        {selectedCustomer.last_order_id && (() => {
                          const orderIdMatch = selectedCustomer.last_order_id.match(/\d+/);
                          const numericOrderId = orderIdMatch ? parseInt(orderIdMatch[0], 10) : null;
                          return numericOrderId ? (
                            <a
                              href={`https://quickmeds.co.uk/dash/orders/${numericOrderId}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                              {selectedCustomer.last_order_id} →
                            </a>
                          ) : (
                            <span className="text-xs font-medium text-gray-900 dark:text-white">
                              {selectedCustomer.last_order_id}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Days Since Last Order:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.daysSinceLastOrder ?? selectedCustomer.days_since_last_order ?? selectedCustomer.customerMetrics?.daysSinceLastOrder ?? '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Account Status */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">Account & Marketing</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Status:</span>
                        {selectedCustomer.is_active !== false ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Marketing Consent:</span>
                        {selectedCustomer.marketing_consent || selectedCustomer.subscribed ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            ✓ Opted In
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                            No
                          </span>
                        )}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Customer Tier:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.customerTier || selectedCustomer.customer_tier || selectedCustomer.customerMetrics?.customerSegment || 'Standard'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Date Joined:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.date_joined
                            ? new Date(selectedCustomer.date_joined).toLocaleDateString('en-GB')
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Last Login:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.last_login
                            ? new Date(selectedCustomer.last_login).toLocaleDateString('en-GB')
                            : 'Never'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Event Tracking */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">Last Event</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Event Type:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white capitalize">
                          {selectedCustomer.lastEventType || selectedCustomer.last_event_type || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Event Date:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {(selectedCustomer.lastEventTypeDateTime || selectedCustomer.last_event_type_date_time)
                            ? new Date(selectedCustomer.lastEventTypeDateTime || selectedCustomer.last_event_type_date_time || '').toLocaleString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-3">
                  {/* Cart Status */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">Cart Information</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Cart Status:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white capitalize">
                          {selectedCustomer.cartStatus || selectedCustomer.cart_status || 'none'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Cart Value:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          £{(selectedCustomer.cartValue || selectedCustomer.cart_value || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Cart Items:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.cartItems || selectedCustomer.cart_items || 0}
                        </span>
                      </div>
                      {(selectedCustomer.cartItemsList || selectedCustomer.cart_items_list) && (
                        <div className="mt-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Items:</span>
                          <p className="text-xs text-gray-900 dark:text-white mt-1">
                            {selectedCustomer.cartItemsList || selectedCustomer.cart_items_list}
                          </p>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Cart Abandoned:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {(selectedCustomer.cartAbandonedDate || selectedCustomer.cart_abandoned_date)
                            ? new Date(selectedCustomer.cartAbandonedDate || selectedCustomer.cart_abandoned_date || '').toLocaleDateString('en-GB')
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Consultation Info */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">Consultation</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Status:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white capitalize">
                          {selectedCustomer.consultationStatus || selectedCustomer.consultation_status || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Type:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {selectedCustomer.consultationType || selectedCustomer.consultation_type || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Started:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {(selectedCustomer.consultationStartedDate || selectedCustomer.consultation_started_date)
                            ? new Date(selectedCustomer.consultationStartedDate || selectedCustomer.consultation_started_date || '').toLocaleDateString('en-GB')
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* GetResponse Sync */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">GetResponse Sync</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Synced:</span>
                        {(selectedCustomer.getresponse_contact_id || selectedCustomer.getresponseContactId) ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            ✓ Yes
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                            No
                          </span>
                        )}
                      </div>
                      {(selectedCustomer.getresponse_contact_id || selectedCustomer.getresponseContactId) && (
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Contact ID:</span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            {selectedCustomer.getresponse_contact_id || selectedCustomer.getresponseContactId}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Last Synced:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {(selectedCustomer.last_synced_to_getresponse || selectedCustomer.lastSyncedToGetresponse)
                            ? new Date(selectedCustomer.last_synced_to_getresponse || selectedCustomer.lastSyncedToGetresponse || '').toLocaleString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Last Attempt:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {(selectedCustomer.last_sync_attempt_to_getresponse || selectedCustomer.lastSyncAttemptToGetresponse)
                            ? new Date(selectedCustomer.last_sync_attempt_to_getresponse || selectedCustomer.lastSyncAttemptToGetresponse || '').toLocaleString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* System Timestamps */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">System Info</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Created At:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {(selectedCustomer.created_at || selectedCustomer.createdAt)
                            ? new Date(selectedCustomer.created_at || selectedCustomer.createdAt || '').toLocaleString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Updated At:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {(selectedCustomer.updated_at || selectedCustomer.updatedAt)
                            ? new Date(selectedCustomer.updated_at || selectedCustomer.updatedAt || '').toLocaleString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Last Synced:</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          {(selectedCustomer.synced_at || selectedCustomer.syncedAt)
                            ? new Date(selectedCustomer.synced_at || selectedCustomer.syncedAt || '').toLocaleString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row gap-2 sm:gap-3 rounded-b-lg">
              <a
                href={`https://quickmeds.co.uk/dash/users/${selectedCustomer.id}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-center text-sm font-medium"
              >
                View in QuickMeds Dashboard →
              </a>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setShowEditModal(true);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium"
              >
                Edit Customer
              </button>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      {showEditModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Edit Customer</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Personal Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      defaultValue={selectedCustomer.first_name}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      defaultValue={selectedCustomer.last_name}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Contact Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      defaultValue={selectedCustomer.email}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      defaultValue={selectedCustomer.phone_number || ''}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Data */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Additional Data
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      defaultValue={
                        selectedCustomer.date_of_birth || selectedCustomer.dob
                          ? new Date(selectedCustomer.date_of_birth || selectedCustomer.dob || '')
                              .toISOString()
                              .split('T')[0]
                          : ''
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Gender
                    </label>
                    <select
                      defaultValue={
                        selectedCustomer.gender === 1 || selectedCustomer.sex === 1
                          ? '1'
                          : selectedCustomer.gender === 0 || selectedCustomer.sex === 0
                          ? '0'
                          : ''
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Not specified</option>
                      <option value="0">Female</option>
                      <option value="1">Male</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Height (cm)
                    </label>
                    <input
                      type="number"
                      defaultValue={selectedCustomer.height || ''}
                      placeholder="e.g., 175"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Marketing Preferences */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Marketing Preferences
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer">
                    <input
                      type="checkbox"
                      defaultChecked={
                        selectedCustomer.marketing_consent || selectedCustomer.subscribed || false
                      }
                      className="w-4 h-4 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">Marketing Consent</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Customer agrees to receive marketing emails and campaigns
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer">
                    <input
                      type="checkbox"
                      defaultChecked={selectedCustomer.is_active !== false}
                      className="w-4 h-4 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">Account Active</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Customer account is active and can place orders
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Read-Only Info */}
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2 text-sm">
                  System Information
                </h4>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Customer ID:</span>
                    <span className="ml-2 font-mono text-gray-900 dark:text-white">
                      {selectedCustomer.id}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Total Orders:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">
                      {selectedCustomer.total_orders}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Joined:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {new Date(selectedCustomer.date_joined).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                    </span>
                  </div>
                  {selectedCustomer.last_login && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Last Login:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">
                        {new Date(selectedCustomer.last_login || '').toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-6 flex gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  alert(
                    '✅ Customer data saved!\n\nNote: This updates MongoDB only. To sync to GetResponse, use the Email Marketing module.'
                  );
                  setShowEditModal(false);
                }}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Customer Modal */}
      {showDeleteModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-600 dark:text-red-400 mb-4">
              Delete Customer?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete{' '}
              <strong>
                {selectedCustomer.first_name} {selectedCustomer.last_name}
              </strong>{' '}
              ({selectedCustomer.email})?
              <br />
              <br />
              <span className="text-red-600 dark:text-red-400 font-semibold">
                ⚠️ This action cannot be undone!
              </span>
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // TODO: Implement delete functionality
                  alert('Delete functionality coming soon!');
                  setShowDeleteModal(false);
                }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete Customer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EshopCustomersTab;
